import { memo, useMemo, useEffect } from 'react'
import { useSelector } from 'react-redux'

// material-ui
import { Grid, Tooltip, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'

// project import
import { DefaultProviderIcon } from 'assets/images/icons'
import { providers } from 'merklebase-utils/commons'
import { selectPriceImageByTicker } from 'store/selectors/prices'
import { cacheImage } from 'utils/iconCache'

// ==============================|| LOADABLE - LAZY LOADING ||============================== //

const Platform = memo(({ platform, text, subtext = '', tooltip, variant = 'body2', disableText }) => {
  const theme = useTheme()

  const provider = useMemo(
    () => Object.values(providers).find((p) => p.ticker === platform),
    [platform]
  )

  const logo = useMemo(() => (provider ? provider?.logo : null), [provider])

  const coinImage = useSelector(useMemo(() => selectPriceImageByTicker(platform), [platform]))

  useEffect(() => {
    if (logo) {
      cacheImage(logo)
    }
    if (coinImage) {
      cacheImage(coinImage)
    }
  }, [logo, coinImage])

  return (
    <Grid container alignItems={'center'} columnSpacing={1} textAlign={'start'}>
      <Grid item>
        <Grid container alignItems={'center'}>
          <Grid item>
            {logo ? (
              <img
                src={logo}
                width={'14px'}
                height={'14px'}
                alt={platform}
                loading="lazy"
                decoding="async"
                style={{ verticalAlign: 'middle' }}
              />
            ) : coinImage ? (
              <img
                src={coinImage}
                alt={platform}
                width={'14px'}
                height={'14px'}
                loading="lazy"
                decoding="async"
                style={{
                  verticalAlign: 'middle',
                }}
              />
            ) : (
              <DefaultProviderIcon />
            )}
          </Grid>
        </Grid>
      </Grid>

      <Grid item sx={{ maxWidth: 'calc(100% - 23px)' }}>
        <Tooltip title={tooltip} placement="top-start" followCursor disableHoverListener={!tooltip}>
          <Grid container>
            {disableText ? null : (
              <Grid item xs={12}>
                <Typography
                  variant={variant}
                  color={theme.palette.common.white}
                  noWrap
                  width={'fit-content'}
                  sx={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: '100%',
                  }}
                >
                  {text || provider?.name}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Tooltip>
      </Grid>
    </Grid>
  )
}, (prev, next) =>
  prev.platform === next.platform &&
  prev.text === next.text &&
  prev.subtext === next.subtext &&
  prev.tooltip === next.tooltip &&
  prev.variant === next.variant &&
  prev.disableText === next.disableText
)
Platform.displayName = 'Platform'

export default Platform
